import type { StockDetail, StorefrontChannel } from '@/lib/api';
import {
  fetchBranchChannels,
  fetchBranchChannelsByCustomUrl,
  fetchBranchInfo,
  fetchProducts,
  fetchStockDetail,
} from '@/lib/api';
import type { BranchInfo, Product } from '@/types/product';
import { create } from 'zustand';
import { branchCache } from './branchCache';
import { useThemeStore } from './themeStore';

interface BranchState {
  customUrl: string | null;
  branchId: string | null;
  branchInfo: BranchInfo | null;
  products: Product[];
  pagination: { total: number; totalPages: number; page: number; limit: number } | null;
  storefrontChannel: StorefrontChannel | null;
  currentStockDetail: StockDetail | null;

  branchLoading: boolean;
  productsLoading: boolean;
  channelLoading: boolean;
  stockDetailLoading: boolean;

  branchError: string | null;
  productsError: string | null;
  channelError: string | null;
  stockDetailError: string | null;

  initialized: boolean;
}

interface BranchActions {
  setBranchId: (branchId: string, customUrl?: string) => void;
  setCustomUrl: (customUrl: string) => void;
  setStorefrontChannel: (channel: StorefrontChannel | null) => void;
  loadBranchInfo: () => Promise<void>;
  loadProducts: (page?: number, limit?: number, append?: boolean) => Promise<void>;
  loadStorefrontChannel: () => Promise<void>;
  clearStore: () => void;
  loadStockDetail: (stockId: string) => Promise<void>;
  resolveByCustomUrl: (customUrl: string) => Promise<void>;
  restoreFromCache: () => boolean;
  setInitialized: () => void;
  persistCache: () => void;
}

interface BranchSelectors {
  getBranchName: () => string;
  getMissionStatement: () => string;
  getHeadline: () => string;
  getDescription: () => string;
  getThemeColor: () => string;
  hasStorefrontData: () => boolean;
}

type BranchStore = BranchState & BranchActions & BranchSelectors;

const initialState: BranchState = {
  customUrl: null,
  branchId: null,
  branchInfo: null,
  products: [],
  pagination: null,
  storefrontChannel: null,
  currentStockDetail: null,
  branchLoading: false,
  productsLoading: false,
  channelLoading: false,
  stockDetailLoading: false,
  branchError: null,
  productsError: null,
  channelError: null,
  stockDetailError: null,
  initialized: false,
};

export const useBranchStore = create<BranchStore>((set, get) => ({
  ...initialState,

  setBranchId: (branchId: string, customUrl?: string) => {
    branchCache.saveBranchId(branchId);
    if (customUrl) {
      branchCache.saveBranchMap(customUrl, branchId);
    }
    set({ branchId, customUrl: customUrl ?? get().customUrl });
  },

  setCustomUrl: (customUrl: string) => {
    set({ customUrl });
  },

  setInitialized: () => {
    set({ initialized: true });
  },

  restoreFromCache: () => {
    const { customUrl } = get();
    let branchId: string | null = null;

    if (customUrl) {
      branchId = branchCache.getBranchIdForCustomUrl(customUrl);
    }
    if (!branchId) {
      branchId = branchCache.getSavedBranchId();
    }

    if (!branchId) return false;

    const cachedData = branchCache.getSavedBranchData(branchId);
    if (cachedData) {
      set({
        branchId,
        branchInfo: cachedData.branchInfo,
        products: cachedData.products,
        pagination: cachedData.pagination,
        storefrontChannel: cachedData.storefrontChannel,
      });

      if (cachedData.storefrontChannel?.themeColor) {
        useThemeStore
          .getState()
          .setThemeColor(cachedData.storefrontChannel.themeColor);
      }
      return true;
    }

    set({ branchId });
    return false;
  },

  resolveByCustomUrl: async (customUrl: string) => {
    set({ channelLoading: true, channelError: null, customUrl });

    try {
      const storefrontChannel = await fetchBranchChannelsByCustomUrl(customUrl);

      if (!storefrontChannel?.branchId) {
        set({
          channelLoading: false,
          channelError: 'No branch found for this URL',
        });
        return;
      }

      const branchId = storefrontChannel.branchId;
      branchCache.saveBranchId(branchId);
      branchCache.saveBranchMap(customUrl, branchId);

      set({
        storefrontChannel,
        branchId,
        channelLoading: false,
      });

      if (storefrontChannel.themeColor) {
        useThemeStore.getState().setThemeColor(storefrontChannel.themeColor);
      }

      get().persistCache();
    } catch (err) {
      set({
        channelError:
          err instanceof Error ? err.message : 'Failed to resolve branch',
        channelLoading: false,
      });
    }
  },

  loadBranchInfo: async () => {
    const { branchId } = get();
    if (!branchId) return;

    set({ branchLoading: true, branchError: null });

    try {
      const branchInfo = await fetchBranchInfo(branchId);
      set({ branchInfo, branchLoading: false });
      get().persistCache();
    } catch (err) {
      set({
        branchError:
          err instanceof Error ? err.message : 'Failed to load branch info',
        branchLoading: false,
      });
    }
  },

  loadProducts: async (page = 1, limit = 30, append = false) => {
    const { branchId, products: currentProducts } = get();
    if (!branchId) return;

    set({ productsLoading: true, productsError: null });

    try {
      const { data: newProducts, pagination } = await fetchProducts(branchId, page, limit);

      const updatedProducts = append ? [...currentProducts, ...newProducts] : newProducts;

      set({
        products: updatedProducts,
        pagination: pagination ?? null,
        productsLoading: false,
      });
      get().persistCache();
    } catch (err) {
      set({
        productsError:
          err instanceof Error ? err.message : 'Failed to load products',
        productsLoading: false,
      });
    }
  },

  loadStorefrontChannel: async () => {
    const { branchId } = get();
    if (!branchId) return;

    set({ channelLoading: true, channelError: null });

    try {
      const storefrontChannel = await fetchBranchChannels(branchId);
      set({ storefrontChannel, channelLoading: false });

      if (storefrontChannel?.themeColor) {
        useThemeStore.getState().setThemeColor(storefrontChannel.themeColor);
      }
      get().persistCache();
    } catch (err) {
      set({
        channelError:
          err instanceof Error ? err.message : 'Failed to load channel',
        channelLoading: false,
      });
    }
  },

  loadStockDetail: async (stockId: string) => {
    set({
      stockDetailLoading: true,
      stockDetailError: null,
      currentStockDetail: null,
    });

    try {
      const stockDetail = await fetchStockDetail(stockId);
      set({ currentStockDetail: stockDetail, stockDetailLoading: false });
    } catch (err) {
      set({
        stockDetailError:
          err instanceof Error ? err.message : 'Failed to load stock detail',
        stockDetailLoading: false,
      });
    }
  },

  persistCache: () => {
    const { branchId, branchInfo, products, storefrontChannel } = get();
    if (branchId) {
      branchCache.saveBranchData(branchId, {
        branchInfo,
        products,
        storefrontChannel,
      });
    }
  },

  getBranchName: () => {
    const { storefrontChannel, branchInfo } = get();
    return storefrontChannel?.name || branchInfo?.name || '';
  },

  getMissionStatement: () => {
    const { storefrontChannel } = get();
    return storefrontChannel?.missionStatement?.trim() || '';
  },

  getHeadline: () => {
    const { storefrontChannel } = get();
    return storefrontChannel?.headline?.trim() || '';
  },

  getDescription: () => {
    const { storefrontChannel } = get();
    return storefrontChannel?.description?.trim() || '';
  },

  getThemeColor: () => {
    const { storefrontChannel } = get();
    return storefrontChannel?.themeColor?.trim() || '#000000';
  },

  hasStorefrontData: () => {
    const { storefrontChannel } = get();
    return !!storefrontChannel;
  },
}));
